import {useCallback, useMemo} from 'react';
import {addDoc, collection, deleteField, doc, getDocs, query, updateDoc, where, writeBatch} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {
    LessonOccurrence,
    LessonOccurrenceDocument,
    LessonOccurrenceInsertOptions,
    LessonOccurrenceUpdateOptions
} from '../types/LessonOccurrence';
import {firestore} from "../App";

// --- Hook ---
interface UseLessonOccurrenceManagerProps {
    plannerId: string;
    timetableId: string;
}

export function useLessonOccurrenceManager({plannerId, timetableId}: UseLessonOccurrenceManagerProps) {

    const occurrencesQuery = useMemo(() => {
        if (!plannerId || !timetableId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'lessonOccurrences')
        );
    }, [plannerId, timetableId]);

    const [occurrencesSnapshot, loadingOccurrences, errorOccurrences] = useCollection(occurrencesQuery);

    const lessonOccurrences = useMemo(() => {
        return occurrencesSnapshot?.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as LessonOccurrence)) || [];
    }, [occurrencesSnapshot]);

    /**
     * Inserts a lesson_occurrence in the database.
     * lessonId is now the first argument.
     */
    const createLessonOccurrence = useCallback(async (
        lessonId: string,
        options: LessonOccurrenceInsertOptions
    ) => {
        if (!plannerId || !timetableId) return;

        const docData: any = {
            plannerId,
            timetableId,
            lessonId,
            ...options,
            instanceExceptions: options.instanceExceptions ?? [],
        };

        // Firestore does not allow 'undefined' values.
        Object.keys(docData).forEach(key => {
            if (docData[key] === undefined) {
                delete docData[key];
            }
        });

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'lessonOccurrences');
        await addDoc(colRef, docData);

    }, [plannerId, timetableId]);

    /**
     * Updates a lesson_occurrence in the database.
     */
    const updateLessonOccurrence = useCallback(async (
        lessonId: string,
        occurrenceId: string,
        options: LessonOccurrenceUpdateOptions
    ) => {
        if (!plannerId || !timetableId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'lessonOccurrences', occurrenceId);

        const updateData = {
            ...options,
            date: options.date ?? deleteField(),
            instanceExceptions: options.instanceExceptions ?? [],
        };

        await updateDoc(docRef, updateData);
    }, [plannerId, timetableId]);

    /**
     * Deletes all occurrences for a lesson and replaces them with a new list.
     */
    const updateLessonOccurrencesForLesson = useCallback(async (
        lessonId: string,
        optionsList: LessonOccurrenceUpdateOptions[]
    ) => {
        if (!plannerId || !timetableId) return;

        const batch = writeBatch(firestore);
        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'lessonOccurrences');

        // 1. Delete all existing for that lessonId
        const q = query(colRef, where("lessonId", "==", lessonId));
        const oldOccurrencesSnap = await getDocs(q);
        oldOccurrencesSnap.forEach(doc => batch.delete(doc.ref));

        // 2. Create all new ones
        optionsList.forEach(options => {
            const newDocRef = doc(colRef);
            const docData: LessonOccurrenceDocument = {
                plannerId,
                timetableId,
                lessonId,
                ...options,
                instanceExceptions: options.instanceExceptions ?? [],
            };

            Object.keys(docData).forEach(key => {
                if ((docData as any)[key] === undefined) {
                    delete (docData as any)[key];
                }
            });

            batch.set(newDocRef, docData);
        });

        await batch.commit();

    }, [plannerId, timetableId]);

    return {
        lessonOccurrences,
        loadingLessonOccurrences: loadingOccurrences, // Rename for clarity
        errorLessonOccurrences: errorOccurrences,
        createLessonOccurrence,
        updateLessonOccurrence,
        updateLessonOccurrencesForLesson
    };
}